import React, { useContext, useEffect, useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button } from '../../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../../pipeMillReportUtils';

import { ReportAdjudicationContext } from './types';
import type { ReportMeasurementItem } from './types';

export const ItemActionButtons: React.FC<{
  measurement: ReportMeasurementItem;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ measurement, onEditMeasurement, onDeleteMeasurement }) => (
  <div className="flex justify-end gap-1">
    <Button type="button" variant="outline" size="sm" title="Edit" onClick={(event) => {
      event.stopPropagation();
      onEditMeasurement?.(measurement);
    }} className="h-8 w-8 p-0">
      <Pencil className="h-3.5 w-3.5" />
    </Button>
    <Button type="button" variant="outline" size="sm" title="Delete" onClick={(event) => {
      event.stopPropagation();
      onDeleteMeasurement?.(measurement);
    }} className="h-8 w-8 p-0 border-readable-red/25 text-readable-red hover:bg-[rgba(196,0,31,0.06)]">
      <Trash2 className="h-3.5 w-3.5" />
    </Button>
  </div>
);

export const StatCard: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; status?: string }> = ({ label, value, sub, status }) => (
  <Card className="border-[#E2E5EA]">
    <CardContent className="p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
      <div className="mt-2 flex items-end justify-between gap-3">
        <div>
          <div className="font-heading text-[24px] font-semibold text-blueprint-blue">{value}</div>
          {sub && <div className="text-[11px] text-heritage-grey">{sub}</div>}
        </div>
        {status && <StatusBadge status={status} />}
      </div>
    </CardContent>
  </Card>
);

export const SectionHead: React.FC<{ title: string; subtitle?: string; status?: string; right?: React.ReactNode }> = ({ title, subtitle, status, right }) => (
  <div className="flex items-start justify-between gap-3 border-b border-[#E2E5EA] bg-white px-4 py-3">
    <div>
      <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">{title}</h3>
      {subtitle && <p className="mt-1 text-[12px] text-heritage-grey">{subtitle}</p>}
    </div>
    {right || (status && <StatusBadge status={status} />)}
  </div>
);

export const ResultRow: React.FC<{ label: string; value: React.ReactNode; unit: string; limit: string; note?: string; status?: string }> = ({
  label,
  value,
  unit,
  limit,
  note,
  status,
}) => (
  <div className="grid grid-cols-1 gap-3 border-b border-[#EEF0F3] px-4 py-3 last:border-b-0 md:grid-cols-[180px_140px_minmax(0,1fr)_130px] md:items-center">
    <div>
      <div className="text-[13px] font-semibold text-blueprint-blue">{label}</div>
      {note && <div className="mt-1 text-[11px] text-heritage-grey">{note}</div>}
    </div>
    <div className="font-mono text-[14px] font-semibold text-text-grey">
      {value}<span className="ml-1 text-[11px] text-heritage-grey">{unit}</span>
    </div>
    <div className="text-[12px] text-heritage-grey">Limit {limit}</div>
    <StatusBadge status={status} />
  </div>
);

export const ChemistryResultRow: React.FC<{
  label: string;
  formula: string;
  value: number;
  max: number;
  status?: string;
}> = ({ label, formula, value, max, status }) => {
  const scaleMax = Math.max(0.5, max + 0.1);
  const valuePercent = Math.min(100, Math.max(0, (value / scaleMax) * 100));
  const limitPercent = Math.min(100, Math.max(0, (max / scaleMax) * 100));

  return (
    <div className="grid grid-cols-1 gap-4 border-b border-[#EEF0F3] px-4 py-4 last:border-b-0 lg:grid-cols-[minmax(180px,260px)_120px_minmax(260px,1fr)_120px] lg:items-center">
      <div>
        <div className="text-[13px] font-semibold text-blueprint-blue">{label}</div>
        <div className="mt-1 max-w-[260px] text-[11px] leading-5 text-heritage-grey">{formula}</div>
      </div>
      <div>
        <div className="font-mono text-[18px] font-semibold text-blueprint-blue">{value.toFixed(2)}</div>
        <div className="mt-1 font-mono text-[11px] text-heritage-grey">limit &lt;= {max.toFixed(2)}</div>
      </div>
      <div className="min-w-0">
        <div className="relative h-3 overflow-hidden rounded-full bg-[#EEF0F3]">
          <div
            className="absolute left-0 top-0 h-full rounded-full bg-[rgba(31,154,58,0.12)]"
            style={{ width: `${valuePercent}%` }}
          />
          <div
            className="absolute top-[-4px] h-5 w-[2px] rounded-full bg-galvanizing-green"
            style={{ left: `${limitPercent}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between font-mono text-[11px] text-heritage-grey">
          <span>0</span>
          <span>{scaleMax.toFixed(2)}</span>
        </div>
      </div>
      <StatusBadge status={status} />
    </div>
  );
};

export const ChemicalFormulaRow: React.FC<{
  label: string;
  formula: string;
  value: number | null;
  max: number | null;
  missing?: string[];
  status?: string;
}> = ({ label, formula, value, max, missing = [], status }) => {
  const hasValue = value != null;
  const hasMax = max != null;
  const scaleMax = Math.max((max || 0) * 1.2, (value || 0) * 1.2, 0.1);
  const valuePercent = hasValue ? Math.min(100, Math.max(0, (value / scaleMax) * 100)) : null;
  const limitPercent = hasMax ? Math.min(100, Math.max(0, (max / scaleMax) * 100)) : null;

  return (
    <div className="grid grid-cols-1 gap-4 border-b border-[#EEF0F3] px-4 py-4 last:border-b-0 lg:grid-cols-[220px_120px_minmax(280px,1fr)_100px] lg:items-center">
      <div>
        <div className="text-[13px] font-semibold text-blueprint-blue">{label}</div>
        <div className="mt-1 max-w-[300px] font-mono text-[10px] leading-5 text-heritage-grey">{formula}</div>
        {missing.length > 0 && (
          <div className="mt-1 text-[10px] text-[#8A6500]">Missing: {missing.join(', ')}</div>
        )}
      </div>
      <div>
        <div className="font-mono text-[18px] font-semibold text-blueprint-blue">{hasValue ? value.toFixed(3) : '-'}</div>
        <div className="mt-1 font-mono text-[11px] text-heritage-grey">{hasMax ? `limit <= ${max}` : 'limit not configured'}</div>
      </div>
      <div className="min-w-0">
        <div className="relative h-3 overflow-hidden rounded-full bg-[#EEF0F3]">
          {limitPercent != null && <div className="absolute left-0 top-0 h-full rounded-full bg-[#E4F6EB]" style={{ width: `${limitPercent}%` }} />}
          {valuePercent != null && <div className="absolute top-[-4px] h-5 w-[2px] rounded-full bg-blueprint-blue" style={{ left: `${valuePercent}%` }} />}
          {limitPercent != null && <div className="absolute top-[-4px] h-5 w-[2px] rounded-full bg-galvanizing-green" style={{ left: `${limitPercent}%` }} />}
        </div>
        <div className="mt-2 flex justify-between font-mono text-[11px] text-heritage-grey">
          <span>0</span>
          <span>{scaleMax.toFixed(3)}</span>
        </div>
      </div>
      <StatusBadge status={status} />
    </div>
  );
};

export const TensileResultRow: React.FC<{
  label: string;
  formula: string;
  value: number;
  unit: string;
  min: number;
  max: number;
  lo?: number;
  hi?: number;
  status?: string;
}> = ({ label, formula, value, unit, min, max, lo, hi, status }) => {
  const hasValue = Number.isFinite(value);
  const valuePercent = hasValue ? Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100)) : 0;
  const loPercent = lo === undefined ? null : Math.min(100, Math.max(0, ((lo - min) / (max - min)) * 100));
  const hiPercent = hi === undefined ? null : Math.min(100, Math.max(0, ((hi - min) / (max - min)) * 100));

  return (
    <div className="grid grid-cols-1 gap-4 border-b border-[#EEF0F3] px-4 py-4 last:border-b-0 lg:grid-cols-[160px_110px_minmax(260px,1fr)_130px] lg:items-center">
      <div>
        <div className="text-[13px] font-semibold text-blueprint-blue">{label}</div>
        <div className="mt-1 text-[11px] text-heritage-grey">{formula}</div>
      </div>
      <div>
        <div className="font-mono text-[18px] font-semibold text-blueprint-blue">
          {hasValue ? value.toFixed(unit === '' ? 3 : 0) : '-'}<span className="ml-1 text-[11px] text-heritage-grey">{hasValue ? unit : ''}</span>
        </div>
        <div className="mt-1 font-mono text-[11px] text-heritage-grey">
          {lo !== undefined && hi !== undefined ? `limit ${lo} - ${hi}` : lo !== undefined ? `limit >= ${lo}` : `limit <= ${hi}`}
        </div>
      </div>
      <div className="min-w-0">
        <div className="relative h-3 overflow-hidden rounded-full bg-[#EEF0F3]">
          <div
            className="absolute top-0 h-full rounded-full bg-[rgba(31,154,58,0.12)]"
            style={{
              left: `${loPercent ?? 0}%`,
              width: `${Math.max(2, (hiPercent ?? valuePercent) - (loPercent ?? 0))}%`,
            }}
          />
          <div
            className={cn('absolute top-[-4px] h-5 w-[2px] rounded-full', status === 'PENDING_REVIEW' ? 'bg-[#B38600]' : 'bg-galvanizing-green')}
            style={{ left: `${valuePercent}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between font-mono text-[11px] text-heritage-grey">
          <span>{min}</span>
          <span>{max}</span>
        </div>
      </div>
      <StatusBadge status={status} />
    </div>
  );
};

export const MiniStatusDot: React.FC<{ status?: string }> = ({ status }) => (
  <span
    className={cn(
      'ml-2 inline-block h-1.5 w-1.5 rounded-full align-middle',
      status === 'PENDING_REVIEW' ? 'bg-[#B38600]' : status === 'FAIL' ? 'bg-readable-red' : 'bg-galvanizing-green'
    )}
  />
);

export const PipeLocationDiagram: React.FC<{
  lengthM: number;
  marks: Array<{ id: string; label: string; position: number; status: string; below?: boolean }>;
}> = ({ lengthM, marks }) => (
  <div className="p-4">
    <div className="relative h-[78px] overflow-hidden rounded-full border border-[#E2E5EA] bg-gradient-to-b from-white to-[#EEF2F5]">
      <div className="absolute left-5 right-5 top-1/2 h-[2px] -translate-y-1/2 bg-[#CAD3DB]" />
      <span className="absolute left-5 top-3 font-mono text-[10px] text-heritage-grey">End 1</span>
      <span className="absolute right-5 top-3 font-mono text-[10px] text-heritage-grey">End 2</span>
      {marks.map((mark) => (
        <span
          key={mark.id}
          className={cn('absolute h-2 w-2 -translate-x-1/2 rounded-full', mark.status === 'PENDING_REVIEW' ? 'bg-[#B38600]' : 'bg-galvanizing-green')}
          style={{ left: `${mark.position * 100}%`, top: mark.below ? '58%' : '42%' }}
          title={mark.label}
        />
      ))}
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-4">
      {marks.map((mark) => (
        <span key={mark.id} className="flex items-center gap-2 font-mono text-[11px] text-heritage-grey">
          <MiniStatusDot status={mark.status} />
          {mark.label}
        </span>
      ))}
      <span className="ml-auto font-mono text-[11px] text-heritage-grey">{lengthM.toFixed(2)} m</span>
    </div>
    <div className="mt-3 text-[11px] text-heritage-grey">Pipe length {lengthM.toFixed(2)} m - weld seam at 12 o'clock</div>
  </div>
);

export const ReportMetadataRail: React.FC<{
  rows: Array<[string, React.ReactNode, boolean?]>;
  extraTitle?: string;
  extraRows?: Array<[string, React.ReactNode, boolean?]>;
  children?: React.ReactNode;
}> = ({ rows, extraTitle, extraRows, children }) => (
  <aside className="border-t border-[#E2E5EA] bg-white p-5 lg:border-l lg:border-t-0">
    <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Report Metadata</div>
    <Card className="mt-3 border-[#E2E5EA]">
      <CardContent className="p-4">
        <KeyValueRows rows={rows} />
      </CardContent>
    </Card>

    {extraRows && (
      <>
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{extraTitle}</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={extraRows} />
          </CardContent>
        </Card>
      </>
    )}

    {children}
  </aside>
);

export const AdjudicationPanel: React.FC = () => {
  const { status, note: savedNote, isSaving, onAdjudicate } = useContext(ReportAdjudicationContext);
  const [note, setNote] = useState(savedNote || '');
  useEffect(() => {
    setNote(savedNote || '');
  }, [savedNote]);
  const saveDisposition = (nextStatus: string, action: string) => {
    if (isSaving) return;
    onAdjudicate?.({ status: nextStatus, action, note: note.trim() });
  };
  const isActionDisabled = (nextStatus: string) =>
    Boolean(isSaving || (status === nextStatus && note.trim() === (savedNote || '').trim()));

  return (
    <>
      <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Adjudication</div>
      <Card className="mt-3 border-[#E2E5EA]">
        <CardContent className="space-y-2 p-4">
          <div className="mb-3 text-[12px] text-text-grey">
            This report is awaiting reviewer disposition.
            {status && <span className="ml-1">Current status: <StatusBadge status={status} /></span>}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => saveDisposition('PASS', 'ACCEPT_AS_PASS')}
            disabled={isActionDisabled('PASS')}
            className="w-full justify-center bg-white text-blueprint-blue disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Check className="mr-2 h-4 w-4" />
            Accept as PASS
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => saveDisposition('FAIL', 'REJECT_AS_FAIL')}
            disabled={isActionDisabled('FAIL')}
            className="w-full justify-center bg-white text-blueprint-blue disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X className="mr-2 h-4 w-4" />
            Reject as FAIL
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => saveDisposition('PENDING_REVIEW', 'MARK_CONDITIONAL')}
            disabled={isActionDisabled('PENDING_REVIEW')}
            className="w-full justify-center bg-white text-blueprint-blue disabled:cursor-not-allowed disabled:opacity-60"
          >
            Mark CONDITIONAL
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => saveDisposition('INCOMPLETE', 'REQUEST_RETEST')}
            disabled={isActionDisabled('INCOMPLETE')}
            className="w-full justify-center text-blueprint-blue disabled:cursor-not-allowed disabled:opacity-60"
          >
            Request retest
          </Button>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Reviewer note..."
            className="mt-2 min-h-[86px] w-full rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 text-[12px] text-text-grey outline-none focus:ring-2 focus:ring-hero-red-10"
          />
        </CardContent>
      </Card>
    </>
  );
};

export const BendDispositionPanel: React.FC = () => (
  <>
    <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Disposition</div>
    <Card className="mt-3 border-galvanizing-green/20 bg-[rgba(31,154,58,0.06)]">
      <CardContent className="p-4">
        <div className="flex items-start gap-2 text-[12px] text-text-grey">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-galvanizing-green" />
          <span>
            All face and root bend specimens reached 180 degrees with open defects below the project limit.
          </span>
        </div>
      </CardContent>
    </Card>
  </>
);

export const FlatteningDispositionPanel: React.FC = () => (
  <>
    <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Disposition</div>
    <Card className="mt-3 border-galvanizing-green/20 bg-[rgba(31,154,58,0.06)]">
      <CardContent className="p-4">
        <div className="flex items-start gap-2 text-[12px] text-text-grey">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-galvanizing-green" />
          <span>
            No cracks or laminations observed during flattening positions. Lot-level flattening requirement is satisfied.
          </span>
        </div>
      </CardContent>
    </Card>
  </>
);

export const HydrostaticDispositionPanel: React.FC = () => (
  <>
    <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Disposition</div>
    <Card className="mt-3 border-galvanizing-green/20 bg-[rgba(31,154,58,0.06)]">
      <CardContent className="p-4">
        <div className="flex items-start gap-2 text-[12px] text-text-grey">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-galvanizing-green" />
          <span>
            Pressure exceeded the project minimum, held for 30 seconds, and no leakage was recorded.
          </span>
        </div>
      </CardContent>
    </Card>
  </>
);

export const KeyValueRows: React.FC<{ rows: Array<[string, React.ReactNode, boolean?]> }> = ({ rows }) => (
  <div className="space-y-2">
    {rows.map(([label, value, mono]) => (
      <div key={label} className="flex justify-between gap-3 border-b border-dashed border-[#E2E5EA] pb-2 last:border-b-0 last:pb-0">
        <span className="text-[12px] text-heritage-grey">{label}</span>
        <span className={cn('text-right text-[12px] font-medium text-text-grey', mono && 'font-mono')}>{value}</span>
      </div>
    ))}
  </div>
);